/**
 * TracedValue — the universal provenance affordance (spine Phase 4).
 *
 * Wraps ANY rendered data value so it can be clicked through to its evidence
 * chain: datapoint → evidence → claim → disclosure, plus hash verification.
 * This is the auditor's mental model (James runs everyone else's flow in
 * reverse) made into a single reusable convention rather than a bespoke page.
 *
 * Contract: pass what the value IS (type + id). With no id, it renders as a
 * plain value with no affordance — so adopting it is always safe, and a value
 * that cannot be traced visibly cannot be traced (that absence is itself the
 * audit signal).
 *
 * Data: GET /v1/audit/lineage/{datapoint_id} · /v1/audit/verify/{evidence_id}
 *
 * Usage:
 *   <TracedValue value="42.5" unit="tCO2e" type="datapoint" id={dp.datapoint_id} />
 *   <TracedValue value={ev.name} type="evidence" id={ev.evidence_id} />
 */
import React, { useState } from 'react';
import { C, api, Badge, Loader } from '../pages/journeys/shared';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const VERIFY_COLOR = { true: C.emerald, false: C.red };

function ChainStep({ label, items, color, emptyNote }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>
        {label}
      </div>
      {(items || []).length === 0 && (
        <div style={{ fontSize: 10, color: C.dim }}>{emptyNote || 'none'}</div>
      )}
      {(items || []).map((it, i) => (
        <div key={it.id || i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', fontSize: 10, borderBottom: `1px solid ${C.border}15` }}>
          <span style={{ color: C.text }}>{it.name || it.label || it.id || '—'}</span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {it.hash && (
              <span style={{ ...MONO, fontSize: 8, color: C.dim }} title={it.hash}>
                {String(it.hash).slice(0, 18)}…
              </span>
            )}
            {it.verified !== undefined && (
              <Badge text={it.verified ? 'VERIFIED' : 'UNVERIFIED'}
                color={VERIFY_COLOR[Boolean(it.verified)]} />
            )}
            {it.id && !it.hash && (
              <span style={{ ...MONO, fontSize: 8, color: C.dim }}>{String(it.id).slice(0, 14)}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function TracedValue({ value, unit, type = 'datapoint', id,
                                      color = C.text, style = {} }) {
  const [open, setOpen] = useState(false);
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // No id → no provenance to show. Render plainly, never a dead affordance.
  if (!id) {
    return (
      <span style={{ ...MONO, color, ...style }}>
        {value}{unit ? ` ${unit}` : ''}
      </span>
    );
  }

  const load = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (trace || loading) return;
    setLoading(true);
    setError(null);
    const path = type === 'evidence'
      ? `/v1/audit/verify/${encodeURIComponent(id)}`
      : `/v1/audit/lineage/${encodeURIComponent(id)}`;
    const r = await api(path);
    if (r?._error) setError(r.message || 'Lineage unavailable');
    else if (type === 'evidence') setTrace(r);
    else {
      // Lineage nests everything under `chain`; requirements arrive as
      // {fw, code} and consolidation as a bare source id — normalize to
      // ChainStep's {name, id, hash, verified} vocabulary here.
      const chain = r?.chain || {};
      setTrace({
        evidence: chain.evidence || [],
        requirements: (chain.requirements || []).map((q) => ({
          id: `${q.fw || ''}:${q.code || ''}`,
          name: `${q.fw || ''} ${q.code || ''}`.trim() || '—',
        })),
        claims: (chain.claims || []).map((c) => ({
          id: c.id,
          name: c.status ? `${c.id} · ${c.status}` : c.id,
        })),
        consolidation: chain.consolidation_source
          ? { id: chain.consolidation_source, name: chain.consolidation_source }
          : null,
        chain_verified: chain.chain_verified,
        chain_complete: chain.chain_complete,
      });
    }
    setLoading(false);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onClick={load}
        title="Trace to evidence"
        style={{
          ...MONO, color, cursor: 'pointer',
          textDecoration: 'underline', textDecorationStyle: 'dotted',
          textDecorationColor: color + '66', textUnderlineOffset: 3, ...style,
        }}
      >
        {value}{unit ? ` ${unit}` : ''}
      </span>

      {open && (
        <div style={{
          position: 'absolute', zIndex: 40, top: '100%', left: 0, marginTop: 6,
          minWidth: 340, maxWidth: 460, maxHeight: 360, overflowY: 'auto',
          padding: '10px 12px', borderRadius: 8, background: C.surface,
          border: `1px solid ${C.borderHi}`, boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          textAlign: 'left', whiteSpace: 'normal',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.blue, display: 'flex', gap: 6, alignItems: 'center' }}>
              Provenance
              {trace && type !== 'evidence' && trace.chain_verified !== undefined && (
                <Badge text={trace.chain_verified ? 'CHAIN VERIFIED' : 'CHAIN UNVERIFIED'}
                  color={VERIFY_COLOR[Boolean(trace.chain_verified)]} />
              )}
            </span>
            <span onClick={(e) => { e.stopPropagation(); setOpen(false); }}
              style={{ cursor: 'pointer', color: C.muted, fontSize: 14, lineHeight: 1, padding: '0 4px' }}>✕</span>
          </div>

          <div style={{ fontSize: 9, color: C.muted, marginBottom: 8, ...MONO }}>
            {type}:{String(id)}
          </div>

          {loading && <Loader text="Tracing..." />}

          {error && (
            <div style={{ fontSize: 10, color: C.amber }}>
              {error}
              <div style={{ color: C.dim, marginTop: 4, fontSize: 9 }}>
                No chain recorded for this value — it cannot be traced to evidence.
              </div>
            </div>
          )}

          {trace && type === 'evidence' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 6 }}>
                <span style={{ color: C.muted }}>Hash integrity</span>
                <Badge text={trace.integrity === 'valid' ? 'VALID' : 'MISMATCH'}
                  color={VERIFY_COLOR[trace.integrity === 'valid']} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginBottom: 6 }}>
                <span style={{ color: C.muted }}>Human verification</span>
                <Badge text={trace.verified ? `VERIFIED${trace.verified_by ? ' · ' + trace.verified_by : ''}` : 'UNVERIFIED'}
                  color={VERIFY_COLOR[Boolean(trace.verified)]} />
              </div>
              {trace.stored_hash && (
                <div style={{ fontSize: 8, color: C.dim, ...MONO, wordBreak: 'break-all' }}>
                  {trace.stored_hash}
                </div>
              )}
              {trace.error && (
                <div style={{ fontSize: 10, color: C.amber, marginTop: 4 }}>{trace.error}</div>
              )}
            </div>
          )}

          {trace && type !== 'evidence' && (
            trace.evidence.length || trace.requirements.length
              || trace.claims.length || trace.consolidation ? (
              <div>
                <ChainStep label="Evidence" items={trace.evidence}
                  emptyNote="no evidence linked — unsubstantiated value" />
                <ChainStep label="Satisfies requirements" items={trace.requirements} />
                <ChainStep label="Supports claims" items={trace.claims} />
                {trace.consolidation && (
                  <ChainStep label="Consolidation" items={[trace.consolidation]} />
                )}
              </div>
            ) : (
              <div style={{ fontSize: 10, color: C.dim }}>
                No lineage recorded for this datapoint.
              </div>
            )
          )}
        </div>
      )}
    </span>
  );
}
